import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DollarSign, Calendar, /* Percent, */ Download, Info, Building2 } from 'lucide-react';
import { DataTable } from '@/components/shared/DataTable';
import { FilterBar, FilterHelpIconPanel } from '@/components/shared/FilterBar';
import {
  applyFilterChangeSelectAll,
  useFilterBarSelectAll,
  type FilterBarChangeMeta,
} from '@/components/shared/filterBarSelectAll';
import {
  inventoryService,
  PolicyFilters,
  PolicyCalculations,
  ReorderPoint,
  InventoryMovementHistory,
  RecommendationChartBar,
} from '@/services/inventoryService';
import {
  InventoryRecommendationChart,
  type RecommendationChartFilter,
  type RecommendationScopeFilterChip,
} from '@/modules/inventoryPolicies/InventoryRecommendationChart';
import {
  categoryBadgeClass,
  resolveCategoryMeta,
  translateActionLabel,
  translateCategoryLabel,
} from '@/modules/inventoryPolicies/recommendationCatalog';
import { translateStockStatusLabel } from '@/modules/inventoryPolicies/stockStatusI18n';
import { ProductDetailSidebar } from '@/components/shared/ProductDetailSidebar';
import { KpiCard } from '@/components/shared/KpiCard';
import type { SupplyTableProduct } from '@/services/supplyService';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
// import { generateInventoryValueHistory } from '@/utils/mockData';
import { useTranslation } from 'react-i18next';
import { useWarehouse, useWarehouseId } from '@/contexts/WarehouseContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useVisibleProductCatFilters } from '@/hooks/useVisibleProductCatFilters';
import { type ProductCatFilterField, buildProductCatApiParams, type ProductCatApiParams } from '@/utils/productCatFilters';
import { useCurrency } from '@/hooks/useCurrency';
import { authService } from '@/services/authService';

function inventoryHealthBarFill(statusOrder?: number): string {
  switch (statusOrder) {
    case 1:
      return '#EF4444';
    case 2:
      return '#EAB308';
    case 3:
      return '#22C55E';
    case 4:
      return '#EAB308';
    case 5:
      return '#EF4444';
    default:
      return '#6B7280';
  }
}

type InventoryPoliciesSelectedFilters = {
  family_ids?: number[];
  stock_status_ids?: number[];
  supplier_ids?: number[];
} & Partial<Record<ProductCatFilterField, string[]>>;

const INVENTORY_MULTI_FILTER_IDS = new Set([
  'family',
  'supplier',
  'stockStatus',
  'cat_1',
  'cat_2',
  'cat_3',
  'cat_4',
  'cat_5',
  'cat_6',
]);

function parseInventoryFilterIdList(values: string[]): number[] {
  return values
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
}

type InventoryCatalogApiFilters = {
  family?: number[];
  supplier_id?: number[];
} & ProductCatApiParams;

function buildInventoryCatalogApiFilters(
  selectedFilters: InventoryPoliciesSelectedFilters,
  selectAllByFilter: Record<string, boolean>,
  visibleCatFilters: ProductCatFilterField[],
): InventoryCatalogApiFilters {
  const result: InventoryCatalogApiFilters = {
    ...(selectedFilters.family_ids?.length && !selectAllByFilter.family
      ? { family: selectedFilters.family_ids }
      : {}),
    ...(selectedFilters.supplier_ids?.length && !selectAllByFilter.supplier
      ? { supplier_id: selectedFilters.supplier_ids }
      : {}),
    ...buildProductCatApiParams(
      selectedFilters,
      visibleCatFilters,
      (field) => Boolean(selectAllByFilter[field]),
    ),
  };
  return result;
}

function appendInventoryCatalogFilterChips(
  target: Array<{ id: string; label: string; value: string }>,
  catalogFilters: InventoryPoliciesSelectedFilters,
  catalog: PolicyFilters,
  visibleCatFilters: ProductCatFilterField[],
  stockStatusLabel: (order?: number | null, catalogName?: string | null) => string,
  selectAllByFilter: Record<string, boolean>,
  catFilterLabel: (field: ProductCatFilterField) => string,
) {
  if (catalogFilters.family_ids?.length) {
    for (const familyId of catalogFilters.family_ids) {
      const family = catalog.families.find((item) => Number(item.value) === familyId);
      if (family) {
        target.push({ id: 'family', label: family.label, value: String(family.value) });
      }
    }
  }

  for (const field of visibleCatFilters) {
    if (selectAllByFilter[field]) {
      target.push({
        id: field,
        label: catFilterLabel(field),
        value: '__all__',
      });
      continue;
    }
    const selected = catalogFilters[field];
    if (!selected?.length) continue;
    const options = catalog[field] ?? [];
    for (const value of selected) {
      const option = options.find((item) => String(item.value) === value);
      if (option) {
        target.push({ id: field, label: option.label, value: String(option.value) });
      }
    }
  }

  if (catalogFilters.stock_status_ids?.length) {
    for (const statusId of catalogFilters.stock_status_ids) {
      const status = catalog.stockStatuses.find((item) => Number(item.value) === statusId);
      if (!status) continue;
      target.push({
        id: 'stockStatus',
        label: stockStatusLabel(status.order, status.label),
        value: String(status.value),
      });
    }
  }

  if (catalogFilters.supplier_ids?.length && catalog.suppliers?.length) {
    for (const supplierId of catalogFilters.supplier_ids) {
      const supplier = catalog.suppliers.find((item) => Number(item.value) === supplierId);
      if (supplier) {
        target.push({ id: 'supplier', label: supplier.label, value: String(supplier.value) });
      }
    }
  }
}

/** Alineado con `FilterBar.fetchOptionsForFilter` (value numérico o string). */
type RemoteProductFilterOption = { value: string | number; label: string };

const SALES_PERIOD_CHOICES = [3, 6, 12, 18, 24] as const;

export function InventoryPolicies() {
  const { t, i18n } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const { isActionEnabled } = useFeatureVisibility();
  const visibleCatFilters = useVisibleProductCatFilters();
  const showRecommendationsChart = isActionEnabled(
    'inventory_policies.recommendations_chart',
    false,
  );
  const warehouseId = useWarehouseId();
  const { loading: warehousesLoading } = useWarehouse();
  const apiOrganizationId = useMemo(
    () => authService.getCurrentUser()?.organization?.id,
    [warehousesLoading],
  );
  const [editMode, setEditMode] = useState(false);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PolicyFilters | null>(null);
  const [calculations, setCalculations] = useState<PolicyCalculations | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<InventoryPoliciesSelectedFilters>({});
  const [activeFilters, setActiveFilters] = useState<Array<{ id: string; label: string; value: string }>>([]);
  const {
    selectAllByFilter,
    updateSelectAllFromChange,
    clearSelectAllFor,
    clearAllSelectAll,
  } = useFilterBarSelectAll();
  const [editedValues, setEditedValues] = useState<Record<string, { reorder_point: number | null }>>({});
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [movementHistoryData, setMovementHistoryData] = useState<InventoryMovementHistory[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<number | undefined>(undefined);
  const [products, setProducts] = useState<RemoteProductFilterOption[]>([]);
  const [selectedSidebarProduct, setSelectedSidebarProduct] = useState<SupplyTableProduct | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Tamaño de página configurable en tabla
  const [tableLoading, setTableLoading] = useState(false); // Loading específico para la tabla
  const [unfilteredInventoryHealth, setUnfilteredInventoryHealth] = useState<PolicyCalculations['inventory_health'] | null>(null);
  const [sorting, setSorting] = useState<{
    field: string | null;
    direction: 'asc' | 'desc';
  }>({ field: null, direction: 'asc' });
  const [recommendationChartBars, setRecommendationChartBars] = useState<RecommendationChartBar[]>([]);
  const [recommendationChartLoading, setRecommendationChartLoading] = useState(true);
  const [recommendationFilter, setRecommendationFilter] = useState<RecommendationChartFilter>(null);
  /** Solo afecta filas de la tabla «Detalle por Producto», no KPIs ni gráficos. */
  const [tablePositiveStockOnly, setTablePositiveStockOnly] = useState(false);

  const remoteProductsCacheRef = useRef<{
    cacheKey: string;
    products: RemoteProductFilterOption[];
  } | null>(null);
  const recommendationChartCacheRef = useRef<{
    cacheKey: string;
    bars: RecommendationChartBar[];
  } | null>(null);

  /** Ventana temporal: ventas para KPI "meses de inventario" e historial de movimientos (mismo N meses). */
  const [trackingMonths, setTrackingMonths] = useState<number>(6);

  const stockStatusLabel = useCallback(
    (order?: number | null, catalogName?: string | null) =>
      translateStockStatusLabel(t, order, catalogName, i18n.language),
    [t, i18n.language],
  );

  const translatedStockStatusOptions = useMemo(() => {
    if (!filters?.stockStatuses?.length) return [];
    return filters.stockStatuses.map((status) => ({
      ...status,
      label: stockStatusLabel(status.order, status.label),
    }));
  }, [filters?.stockStatuses, stockStatusLabel]);

  const inventoryHealthUsesTableScope = useMemo(
    () =>
      Boolean(recommendationFilter)
      || !selectedFilters.stock_status_ids?.length
      || Boolean(selectAllByFilter.stockStatus),
    [recommendationFilter, selectedFilters.stock_status_ids, selectAllByFilter.stockStatus],
  );

  const inventoryHealthForChart = useMemo(() => {
    const src = inventoryHealthUsesTableScope
      ? (calculations?.inventory_health ?? [])
      : (unfilteredInventoryHealth ?? calculations?.inventory_health ?? []);
    return src
      .map((status) => ({
        name: stockStatusLabel(status.status_order, status.status),
        percentage: status.percentage,
        count: status.count,
        status_id: status.status_id,
        status_order: status.status_order,
      }))
      .sort((a, b) => (a.status_order ?? 999) - (b.status_order ?? 999));
  }, [
    inventoryHealthUsesTableScope,
    unfilteredInventoryHealth,
    calculations?.inventory_health,
    stockStatusLabel,
  ]);

  const inventoryHealthTotalRefs = useMemo(
    () => inventoryHealthForChart.reduce((sum, s) => sum + (Number(s.count) || 0), 0),
    [inventoryHealthForChart],
  );

  const catalogApiFilters = useMemo(
    () => buildInventoryCatalogApiFilters(selectedFilters, selectAllByFilter, visibleCatFilters),
    [selectedFilters, selectAllByFilter, visibleCatFilters],
  );

  const policyApiFilters = useMemo(
    () => ({
      ...catalogApiFilters,
      ...(selectedFilters.stock_status_ids?.length && !selectAllByFilter.stockStatus
        ? { stock_status: selectedFilters.stock_status_ids }
        : {}),
      ...(showRecommendationsChart && recommendationFilter
        ? {
            ...(recommendationFilter.categoryKey
              ? { action_category_key: recommendationFilter.categoryKey }
              : {}),
            ...(recommendationFilter.onDemand !== null
              ? { recommendation_on_demand: recommendationFilter.onDemand }
              : {}),
          }
        : {}),
    }),
    [catalogApiFilters, selectedFilters.stock_status_ids, selectAllByFilter.stockStatus, recommendationFilter, showRecommendationsChart],
  );

  const recommendationChartApiFilters = useMemo(
    () => ({
      ...catalogApiFilters,
      ...(selectedFilters.stock_status_ids?.length && !selectAllByFilter.stockStatus
        ? { stock_status: selectedFilters.stock_status_ids }
        : {}),
      ...(recommendationFilter?.categoryKey === null &&
      recommendationFilter.onDemand !== null
        ? { recommendation_on_demand: recommendationFilter.onDemand }
        : {}),
    }),
    [catalogApiFilters, selectedFilters.stock_status_ids, selectAllByFilter.stockStatus, recommendationFilter],
  );

  const recommendationChartScopeChips = useMemo((): RecommendationScopeFilterChip[] => {
    const chips: RecommendationScopeFilterChip[] = [];
    if (!filters) return chips;

    if (selectedFilters.family_ids?.length) {
      for (const familyId of selectedFilters.family_ids) {
        const family = filters.families.find((item) => Number(item.value) === familyId);
        if (family) chips.push({ id: 'family', label: family.label });
      }
    }

    for (const field of visibleCatFilters) {
      if (selectAllByFilter[field]) {
        chips.push({ id: field, label: t(`productCatFilters.${field}`) });
        continue;
      }
      const selected = selectedFilters[field];
      if (!selected?.length || !filters) continue;
      for (const value of selected) {
        const option = (filters[field] ?? []).find((item) => String(item.value) === value);
        if (option) chips.push({ id: field, label: option.label });
      }
    }

    if (selectedFilters.stock_status_ids?.length) {
      for (const sid of selectedFilters.stock_status_ids) {
        const status = filters.stockStatuses.find((s) => Number(s.value) === sid);
        if (!status) continue;
        chips.push({
          id: `stockStatus-${sid}`,
          label: stockStatusLabel(status.order, status.label),
          accentColor: inventoryHealthBarFill(status.order),
        });
      }
    }

    if (selectedFilters.supplier_ids?.length && filters.suppliers?.length) {
      for (const supplierId of selectedFilters.supplier_ids) {
        const supplier = filters.suppliers.find((item) => Number(item.value) === supplierId);
        if (supplier) chips.push({ id: 'supplier', label: supplier.label });
      }
    }

    return chips;
  }, [filters, selectedFilters, stockStatusLabel, visibleCatFilters, selectAllByFilter, t]);

  const stockStatusFilterHelpList = useMemo(() => {
    const items: { label: string; text: string }[] = [];
    let intro: string | undefined;
    if (!filters?.stockStatuses?.length) {
      intro = t('inventoryPolicies.filters.stockStatusHelpEmpty');
    } else {
      intro = t('inventoryPolicies.filters.stockStatusHelpIntro');
      for (const s of filters.stockStatuses) {
        const desc = (s.description ?? '').trim();
        items.push({
          label: stockStatusLabel(s.order, s.label),
          text: desc || t('inventoryPolicies.filters.stockStatusNoCatalogDescription'),
        });
      }
    }
    return { intro, items };
  }, [filters?.stockStatuses, stockStatusLabel, t]);

  // const inventoryHistory = generateInventoryValueHistory();
  
  // Mapeo de estados de inventario
  const inventoryStatusMap = {
    'Óptimo': {
      key: 'optimal',
      color: 'bg-green-100 text-green-800'
    },
    'Obsoleto': {
      key: 'obsolete',
      color: 'bg-red-100 text-red-800'
    },
    'Sobre inventario': {
      key: 'overstock',
      color: 'bg-yellow-100 text-yellow-800'
    },
    'Overstock': {
      key: 'overstock',
      color: 'bg-yellow-100 text-yellow-800'
    },
    'Bajo mínimos': {
      key: 'underMinimum',
      color: 'bg-yellow-100 text-yellow-800'
    },
    'Sin Stock': {
      key: 'noStock',
      color: 'bg-red-100 text-red-800'
    },
    'Stockout': {
      key: 'stockout',
      color: 'bg-red-100 text-red-800'
    },
    'Sin Rotación': {
      key: 'noRotation',
      color: 'bg-gray-100 text-gray-800'
    }
  };
  
  // Mapeo de colores para el gráfico de salud del inventario (ya no se usa, se usa status_order)
  // const inventoryHealthChartColors = {
  //   'Óptimo': 'bg-green-500',
  //   'Overstock': 'bg-yellow-500',
  //   'Stockout': 'bg-red-500',
  //   'Sin Rotación': 'bg-purple-500'
  // };
  
  
  // Catálogo de filtros (proveedores dependen del almacén activo)
  useEffect(() => {
    const loadFilters = async () => {
      if (warehousesLoading || warehouseId === null) return;
      try {
        const filterOptions = await inventoryService.getPolicyFilters({
          warehouse_id: warehouseId,
          ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
        });
        setFilters(filterOptions);
      } catch {
        // Silencioso: no bloquear la página si falla cargar filtros
      }
    };
    loadFilters();
  }, [apiOrganizationId, warehouseId, warehousesLoading]);

  useEffect(() => {
    if (!filters?.stockStatuses?.length) return;
    setActiveFilters((prev) => {
      let changed = false;
      const next = prev.map((chip) => {
        if (!chip.id.startsWith('stockStatus-')) return chip;
        const statusId = Number(chip.id.replace('stockStatus-', ''));
        const status = filters.stockStatuses.find((s) => Number(s.value) === statusId);
        if (!status) return chip;
        const label = stockStatusLabel(status.order, status.label);
        if (label === chip.label) return chip;
        changed = true;
        return { ...chip, label };
      });
      return changed ? next : prev;
    });
  }, [i18n.language, filters?.stockStatuses, stockStatusLabel]);

  // Chip por defecto del periodo de seguimiento cuando ya hay opciones de catálogo
  useEffect(() => {
    if (!filters) return;
    setActiveFilters((prev) => {
      if (prev.some((f) => f.id === 'salesPeriod')) return prev;
      return [
        ...prev,
        {
          id: 'salesPeriod',
          label: t('inventoryPolicies.filters.salesPeriodOption', { count: 6 }),
          value: '6',
        },
      ];
    });
  }, [filters, t]);

  // Fetch data cuando cambian filtros/producto/almacén
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Si solo cambia la página, usar loading de tabla, sino loading general
        const isPageChange = calculations !== null && calculations.reorder_points.length > 0;
        if (isPageChange) {
          setTableLoading(true);
        } else {
          setKpisLoading(true);
        }

        if (selectedProduct) {
          setHistoryLoading(true);
        } else {
          setHistoryLoading(false);
        }

        const commonWarehouseId = warehouseId === null ? undefined : warehouseId;
        const orderingParam = sorting.field 
          ? `${sorting.direction === 'desc' ? '-' : ''}${sorting.field}`
          : undefined;

        // Parámetros base (estado de stock admite varios IDs en API)
        const calcParams: Parameters<typeof inventoryService.getPolicyCalculations>[0] = {
          ...policyApiFilters,
          product_id: selectedProduct,
          warehouse_id: commonWarehouseId,
          page: currentPage,
          page_size: pageSize,
          ordering: orderingParam,
          sales_months: trackingMonths,
          ...(tablePositiveStockOnly ? { positive_stock_only: true } : {}),
          ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
        };

        const tableCalcParams = calcParams;

        const policyCalculationsPromise = inventoryService.getPolicyCalculations(tableCalcParams);
        const movementHistoryPromise = selectedProduct
          ? inventoryService.getInventoryMovementHistory({
              product_id: selectedProduct,
              months: trackingMonths,
              ...buildInventoryCatalogApiFilters(selectedFilters, selectAllByFilter, visibleCatFilters),
              ...(selectedFilters.stock_status_ids?.length && !selectAllByFilter.stockStatus
                ? { stock_status: selectedFilters.stock_status_ids }
                : {}),
              warehouse_id: commonWarehouseId,
              ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
            })
          : Promise.resolve([] as InventoryMovementHistory[]);

        const promises: Promise<any>[] = [policyCalculationsPromise, movementHistoryPromise];

        let unfilteredHealthIdx = -1;
        if (selectedFilters.stock_status_ids?.length && !selectAllByFilter.stockStatus && !recommendationFilter) {
          unfilteredHealthIdx = promises.length;
          promises.push(
            inventoryService.getPolicyCalculations({
              ...catalogApiFilters,
              product_id: selectedProduct,
              warehouse_id: commonWarehouseId,
              only_kpis: true,
              sales_months: trackingMonths,
              ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
            })
          );
        }

        const results = await Promise.all(promises);
        let r = 0;
        const policyCalculations = results[r++];
        const movementHistory = results[r++] as InventoryMovementHistory[];
        if (unfilteredHealthIdx >= 0) {
          const unfilteredHealthData = results[r++];
          setUnfilteredInventoryHealth(unfilteredHealthData.inventory_health ?? policyCalculations.inventory_health);
        } else {
          setUnfilteredInventoryHealth(policyCalculations.inventory_health);
        }

        setCalculations((prev) => ({
          ...prev,
          ...policyCalculations,
          inventory_health: policyCalculations.inventory_health ?? [],
        }));
        setKpisLoading(false);

        setMovementHistoryData(movementHistory);
        setHistoryLoading(false);
        setError(null);
        setTableLoading(false);
      } catch (err) {
        console.error('Error fetching inventory policy data:', err);
        setError('Failed to load inventory policy data. Please try again later.');
        setKpisLoading(false);
        setHistoryLoading(false);
        setTableLoading(false);
      }
    };

    if (warehousesLoading || warehouseId === null) return;
    fetchData();
  }, [selectedFilters, selectAllByFilter, selectedProduct, warehouseId, warehousesLoading, currentPage, pageSize, sorting, trackingMonths, apiOrganizationId, recommendationFilter, showRecommendationsChart, tablePositiveStockOnly, catalogApiFilters, policyApiFilters, visibleCatFilters]);

  useEffect(() => {
    if (!showRecommendationsChart) {
      setRecommendationFilter(null);
      setActiveFilters((prev) => prev.filter((f) => f.id !== 'recommendation'));
    }
  }, [showRecommendationsChart]);

  useEffect(() => {
    const loadRecommendationChart = async () => {
      if (
        !showRecommendationsChart ||
        warehousesLoading ||
        warehouseId === null ||
        selectedProduct
      ) {
        setRecommendationChartBars([]);
        setRecommendationChartLoading(false);
        return;
      }
      const cacheKey = JSON.stringify({
        ...recommendationChartApiFilters,
        warehouseId,
        apiOrganizationId,
      });
      if (recommendationChartCacheRef.current?.cacheKey === cacheKey) {
        setRecommendationChartBars(recommendationChartCacheRef.current.bars);
        setRecommendationChartLoading(false);
        return;
      }
      try {
        setRecommendationChartLoading(true);
        const chartData = await inventoryService.getRecommendationChart({
          ...recommendationChartApiFilters,
          warehouse_id: warehouseId,
          ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
        });
        const bars = chartData.bars ?? [];
        recommendationChartCacheRef.current = { cacheKey, bars };
        setRecommendationChartBars(bars);
      } catch {
        setRecommendationChartBars([]);
      } finally {
        setRecommendationChartLoading(false);
      }
    };
    loadRecommendationChart();
  }, [
    recommendationChartApiFilters,
    warehouseId,
    warehousesLoading,
    selectedProduct,
    apiOrganizationId,
    showRecommendationsChart,
  ]);

  // Al cambiar estado de stock (p. ej. clic en salud), quitar filtro de segmento del gráfico de recomendaciones.
  useEffect(() => {
    setRecommendationFilter(null);
    setActiveFilters((prev) => prev.filter((f) => f.id !== 'recommendation'));
  }, [selectedFilters.stock_status_ids]);

  useEffect(() => {
    if (warehousesLoading) return;
    if (warehouseId === null) {
      setKpisLoading(false);
      setTableLoading(false);
      setHistoryLoading(false);
    }
  }, [warehousesLoading, warehouseId]);

  useEffect(() => {
    remoteProductsCacheRef.current = null;
    recommendationChartCacheRef.current = null;
    setProducts([]);
  }, [
    warehouseId,
    selectedFilters.family_ids,
    selectedFilters.supplier_ids,
    ...visibleCatFilters.flatMap((field) => [
      selectedFilters[field],
      selectAllByFilter[field],
    ]),
    apiOrganizationId,
  ]);

  const fetchProductOptionsForFilter = useCallback(
    async (_filterId: string, searchQuery: string): Promise<RemoteProductFilterOption[]> => {
      const wid = warehouseId === null ? undefined : warehouseId;
      if (!wid) return [];
      const familyKey = selectedFilters.family_ids?.length
        ? selectedFilters.family_ids.join('-')
        : 'all';
      const catKey = visibleCatFilters
        .map((field) => (
          selectAllByFilter[field]
            ? `${field}:all`
            : selectedFilters[field]?.length
              ? selectedFilters[field]!.join('-')
              : 'none'
        ))
        .join('_');
      const cacheKey = `${apiOrganizationId ?? 'na'}_${wid}_${familyKey}_${catKey}`;
      const cached =
        remoteProductsCacheRef.current?.cacheKey === cacheKey
          ? remoteProductsCacheRef.current.products
          : null;
      let list: RemoteProductFilterOption[];
      if (cached) {
        list = cached;
      } else {
        const res = await inventoryService.getPolicyFilters({
          warehouse_id: wid,
          ...buildInventoryCatalogApiFilters(selectedFilters, selectAllByFilter, visibleCatFilters),
          include_products: true,
          ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
        });
        list = res.products ?? [];
        remoteProductsCacheRef.current = { cacheKey, products: list };
        setProducts(list);
      }
      const q = searchQuery.trim().toLowerCase();
      if (!q) return list;
      return list.filter((p) => p.label.toLowerCase().includes(q));
    },
    [warehouseId, selectedFilters, selectAllByFilter, visibleCatFilters, apiOrganizationId]
  );
  
  // Filter options for the FilterBar component
  const filterOptions = useMemo(() => {
    if (!filters) return [];
    const base = [
      {
        id: 'salesPeriod',
        name: t('inventoryPolicies.filters.salesPeriod'),
        options: SALES_PERIOD_CHOICES.map((m) => ({
          value: m,
          label: t('inventoryPolicies.filters.salesPeriodOption', { count: m }),
        })),
        multiple: false,
      },
      {
        id: 'product',
        name: t('inventoryPolicies.filters.product'),
        options: products,
        loadOptionsRemotely: true,
        multiple: false,
      },
      {
        id: 'family',
        name: t('inventoryPolicies.filters.family'),
        options: filters.families,
        multiple: true,
      },
      ...visibleCatFilters.map((field) => ({
        id: field,
        name: t(`productCatFilters.${field}`),
        options: filters[field] ?? [],
        multiple: true,
      })),
      {
        id: 'supplier',
        name: t('inventoryPolicies.filters.supplier'),
        options: filters.suppliers ?? [],
        multiple: true,
      },
      {
        id: 'stockStatus',
        name: t('inventoryPolicies.filters.stockStatus'),
        helpList: stockStatusFilterHelpList,
        options: translatedStockStatusOptions,
        multiple: true,
      },
    ];
    return base;
  }, [filters, products, visibleCatFilters, stockStatusFilterHelpList, translatedStockStatusOptions, t]);
  
  const handleFilterChange = (
    newFilters: Record<string, string[]>,
    meta?: FilterBarChangeMeta,
  ) => {
    const nextSelectAllByFilter = applyFilterChangeSelectAll(selectAllByFilter, newFilters, meta);
    updateSelectAllFromChange(newFilters, meta);
    const parseFilterValue = (value: string | undefined): number | undefined => {
      if (!value) return undefined;
      const parsed = Number(value);
      return isNaN(parsed) ? undefined : parsed;
    };

    // Filtros múltiples: familia, categoría, proveedor y estado de stock.
    const incomingFilters: Record<string, string[]> = {};
    Object.entries(newFilters).forEach(([key, values]) => {
      if (INVENTORY_MULTI_FILTER_IDS.has(key)) {
        incomingFilters[key] = values;
      } else {
        incomingFilters[key] = values.length > 0 ? [values[0]] : [];
      }
    });

    // Combinar con filtros existentes, pero reemplazar los que vienen en newFilters
    const updatedFilters = { ...selectedFilters };
    let updatedSelectedProduct = selectedProduct;
    let updatedTrackingMonths = trackingMonths;

    Object.entries(incomingFilters).forEach(([filterKey, filterValues]) => {
      if (filterValues.length === 0) {
        switch (filterKey) {
          case 'salesPeriod':
            updatedTrackingMonths = 6;
            break;
          case 'product':
            updatedSelectedProduct = undefined;
            break;
          case 'family':
            delete updatedFilters.family_ids;
            break;
          case 'stockStatus':
            delete updatedFilters.stock_status_ids;
            break;
          case 'supplier':
            delete updatedFilters.supplier_ids;
            break;
          default:
            if (visibleCatFilters.includes(filterKey as ProductCatFilterField)) {
              delete updatedFilters[filterKey as ProductCatFilterField];
            }
            break;
        }
      } else {
        switch (filterKey) {
          case 'salesPeriod': {
            const m = parseFilterValue(filterValues[0]);
            updatedTrackingMonths =
              m !== undefined && (SALES_PERIOD_CHOICES as readonly number[]).includes(m) ? m : 6;
            break;
          }
          case 'product':
            updatedSelectedProduct = parseFilterValue(filterValues[0]);
            break;
          case 'family': {
            const ids = parseInventoryFilterIdList(filterValues);
            if (ids.length === 0) delete updatedFilters.family_ids;
            else updatedFilters.family_ids = ids;
            break;
          }
          case 'stockStatus': {
            const ids = parseInventoryFilterIdList(filterValues);
            if (ids.length === 0) delete updatedFilters.stock_status_ids;
            else updatedFilters.stock_status_ids = ids;
            break;
          }
          case 'supplier': {
            const ids = parseInventoryFilterIdList(filterValues);
            if (ids.length === 0) delete updatedFilters.supplier_ids;
            else updatedFilters.supplier_ids = ids;
            break;
          }
          default: {
            if (visibleCatFilters.includes(filterKey as ProductCatFilterField)) {
              const values = filterValues.map((v) => v.trim()).filter(Boolean);
              if (values.length === 0) delete updatedFilters[filterKey as ProductCatFilterField];
              else updatedFilters[filterKey as ProductCatFilterField] = values;
            }
            break;
          }
        }
      }
    });

    // Reconstruir activeFilters basándose en TODOS los filtros activos
    const newActiveFilters: Array<{ id: string; label: string; value: string }> = [];
    
    // Agregar el filtro de producto si está seleccionado
    if (updatedSelectedProduct) {
      const product = products.find((p) => p.value === updatedSelectedProduct);
      const labelFromMeta =
        meta?.selectedLabel?.filterId === 'product' ? meta.selectedLabel.label : undefined;
      const label = product?.label ?? labelFromMeta;
      if (label) {
        newActiveFilters.push({
          id: 'product',
          label,
          value: String(updatedSelectedProduct),
        });
      }
    }
    
    if (filters) {
      appendInventoryCatalogFilterChips(
        newActiveFilters,
        updatedFilters,
        filters,
        visibleCatFilters,
        stockStatusLabel,
        nextSelectAllByFilter,
        (field) => t(`productCatFilters.${field}`),
      );
    }

    newActiveFilters.push({
      id: 'salesPeriod',
      label: t('inventoryPolicies.filters.salesPeriodOption', { count: updatedTrackingMonths }),
      value: String(updatedTrackingMonths),
    });

    // Actualizar estados
    setActiveFilters(newActiveFilters);
    setSelectedFilters(updatedFilters);
    setSelectedProduct(updatedSelectedProduct);
    setTrackingMonths(updatedTrackingMonths);
    setCurrentPage(1); // Resetear a la primera página cuando cambien los filtros
  };

  const handleClearFilter = (filterId: string) => {
    let updatedSelectedProduct = selectedProduct;
    let updatedFilters = { ...selectedFilters };
    const nextTrackingMonths = filterId === 'salesPeriod' ? 6 : trackingMonths;

    // Eliminar el filtro específico
    switch (filterId) {
      case 'salesPeriod':
        break;
      case 'product':
        updatedSelectedProduct = undefined;
        break;
      case 'family':
        delete updatedFilters.family_ids;
        clearSelectAllFor('family');
        break;
      case 'stockStatus':
        delete updatedFilters.stock_status_ids;
        clearSelectAllFor('stockStatus');
        break;
      case 'supplier':
        delete updatedFilters.supplier_ids;
        clearSelectAllFor('supplier');
        break;
      case 'recommendation':
        setRecommendationFilter(null);
        break;
      default:
        if (visibleCatFilters.includes(filterId as ProductCatFilterField)) {
          delete updatedFilters[filterId as ProductCatFilterField];
          clearSelectAllFor(filterId);
        }
        break;
    }

    // Reconstruir activeFilters basándose en todos los filtros restantes
    const newActiveFilters: Array<{ id: string; label: string; value: string }> = [];
    
    // Agregar el filtro de producto si está seleccionado y no es el que se está eliminando
    if (updatedSelectedProduct && filterId !== 'product') {
      const product = products.find(p => p.value === updatedSelectedProduct);
      if (product) {
        newActiveFilters.push({
          id: 'product',
          label: product.label,
          value: String(product.value)
        });
      }
    }
    
    if (filters) {
      appendInventoryCatalogFilterChips(
        newActiveFilters,
        updatedFilters,
        filters,
        visibleCatFilters,
        stockStatusLabel,
        selectAllByFilter,
        (field) => t(`productCatFilters.${field}`),
      );
    }

    newActiveFilters.push({
      id: 'salesPeriod',
      label: t('inventoryPolicies.filters.salesPeriodOption', { count: nextTrackingMonths }),
      value: String(nextTrackingMonths),
    });

    // Actualizar estados
    setActiveFilters(newActiveFilters);
    setSelectedFilters(updatedFilters);
    setSelectedProduct(updatedSelectedProduct);
    setTrackingMonths(nextTrackingMonths);
    setCurrentPage(1); // Resetear a la primera página cuando cambien los filtros
  };

  const handleClearAllFilters = () => {
    clearAllSelectAll();
    setSelectedFilters({});
    setSelectedProduct(undefined);
    setRecommendationFilter(null);
    setTrackingMonths(6);
    setActiveFilters([
      {
        id: 'salesPeriod',
        label: t('inventoryPolicies.filters.salesPeriodOption', { count: 6 }),
        value: '6',
      },
    ]);
    setCurrentPage(1); // Resetear a la primera página cuando se limpien todos los filtros
  };

  // Handler para ordenamiento del servidor
  const handleSort = (field: string) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1); // Resetear a la primera página cuando cambie el ordenamiento
  };

  // Función para manejar el clic en una barra del gráfico de salud
  const clearRecommendationFilter = () => {
    setRecommendationFilter(null);
    setCurrentPage(1);
    setActiveFilters((prev) => prev.filter((f) => f.id !== 'recommendation'));
  };

  const applyRecommendationFilter = (
    categoryKey: string | null,
    onDemand: boolean | null,
    chipLabel: string,
    chipValue: string,
  ) => {
    setRecommendationFilter({ categoryKey, onDemand });
    setCurrentPage(1);
    setActiveFilters((prev) => {
      const without = prev.filter((f) => f.id !== 'recommendation');
      return [...without, { id: 'recommendation', label: chipLabel, value: chipValue }];
    });
  };

  const handleRecommendationCategoryClick = (categoryKey: string) => {
    if (
      recommendationFilter?.categoryKey === categoryKey &&
      recommendationFilter.onDemand === null
    ) {
      clearRecommendationFilter();
      return;
    }
    const categoryLabel = translateCategoryLabel(t, categoryKey);
    applyRecommendationFilter(
      categoryKey,
      null,
      categoryLabel,
      `${categoryKey}:all`,
    );
  };

  const handleRecommendationSegmentClick = (categoryKey: string, onDemand: boolean) => {
    if (
      recommendationFilter?.categoryKey === categoryKey &&
      recommendationFilter.onDemand === onDemand
    ) {
      clearRecommendationFilter();
      return;
    }
    const categoryLabel = translateCategoryLabel(t, categoryKey);
    const segmentLabel = onDemand
      ? t('inventoryPolicies.recommendations.chart.onDemand')
      : t('inventoryPolicies.recommendations.chart.stock');
    applyRecommendationFilter(
      categoryKey,
      onDemand,
      `${categoryLabel} · ${segmentLabel}`,
      `${categoryKey}:${onDemand ? '1' : '0'}`,
    );
  };

  const handleGlobalRecommendationSegmentClick = (onDemand: boolean) => {
    if (
      recommendationFilter?.categoryKey === null &&
      recommendationFilter.onDemand === onDemand
    ) {
      clearRecommendationFilter();
      return;
    }
    const segmentLabel = onDemand
      ? t('inventoryPolicies.recommendations.chart.onDemand')
      : t('inventoryPolicies.recommendations.chart.stock');
    applyRecommendationFilter(
      null,
      onDemand,
      segmentLabel,
      `segment:${onDemand ? '1' : '0'}`,
    );
  };

  const handleHealthBarClick = (statusOrder: number, statusId?: number | null) => {
    if (!filters) return;

    let matchingStatus =
      statusId != null
        ? filters.stockStatuses.find((status) => Number(status.value) === statusId)
        : undefined;

    if (!matchingStatus) {
      matchingStatus = filters.stockStatuses.find((status) => status.order === statusOrder);
    }

    if (!matchingStatus) {
      const healthRow = (
        unfilteredInventoryHealth ??
        calculations?.inventory_health ??
        []
      ).find((h) => h.status_order === statusOrder);
      const catalogName = healthRow?.status;
      if (catalogName) {
        matchingStatus = filters.stockStatuses.find(
          (status) => status.label.toLowerCase() === catalogName.toLowerCase(),
        );
      }
    }

    if (matchingStatus) {
      handleFilterChange({
        stockStatus: [String(matchingStatus.value)],
      });
    } else {
      console.warn(`No matching stock status for order: ${statusOrder}`);
    }
  };
  
  const handleEditChange = (productId: number, value: string) => {
    // Convertir string vacío a null, y otros valores a número
    const numericValue = value === '' ? null : Number(value);
    
    setEditedValues(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        reorder_point: numericValue
      }
    }));
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      
      // Filtrar solo los productos con cambios reales
      const changedProducts = Object.entries(editedValues).filter(([productId, editedData]) => {
        const originalData = calculations?.reorder_points.find(p => p.id === Number(productId));
        if (!originalData) return false;
        
        // Verificar si realmente hay cambios (solo reorder_point)
        const hasReorderPointChange = editedData.reorder_point !== undefined &&
          editedData.reorder_point !== originalData.inventory_policy__reorder_point;
        return hasReorderPointChange;
      });
      
            if (changedProducts.length === 0) {
        // No hay cambios reales, solo salir del modo edición
        setEditMode(false);
        setEditedValues({});
        setSaveSuccess(t('inventoryPolicies.reorderPoints.noChanges'));
        setTimeout(() => setSaveSuccess(null), 3000);
        return;
      }
      
      // Enviar solo los productos con cambios reales (solo reorder_point)

      const updates = changedProducts.map(([productId, values]) => {
        const originalData = calculations?.reorder_points.find(p => p.id === Number(productId));
        const updateData = {
          reorder_point: values.reorder_point !== undefined ?
            values.reorder_point :
            (originalData?.inventory_policy__reorder_point ?? null)
        };
        return inventoryService.updateReorderPoints(
          Number(productId),
          updateData,
          warehouseId === null ? undefined : warehouseId
        );
      });
      
      const results = await Promise.all(updates);
      
      // Recargar los datos
      const policyCalculations = await inventoryService.getPolicyCalculations({
        ...policyApiFilters,
        product_id: selectedProduct,
        warehouse_id: warehouseId === null ? undefined : warehouseId,
        sales_months: trackingMonths,
        ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
      });
      setCalculations(policyCalculations);
      
      // Limpiar el modo de edición y los valores editados
      setEditMode(false);
      setEditedValues({});
      
      // Mostrar mensaje de éxito
      const changedCount = results.filter(result => result.changes_detected !== false).length;
      
      setSaveSuccess(t('inventoryPolicies.reorderPoints.savedSuccessfully', { count: changedCount }));
      setTimeout(() => setSaveSuccess(null), 5000);
      
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const mapReorderPointToSidebarProduct = (item: ReorderPoint): SupplyTableProduct => ({
    id: item.id,
    code: item.code,
    name: item.name,
    family: '',
    family_id: null,
    category: '',
    lifecycle_stage: { name: '', order: 0 },
    abc_category: '',
    stock_actual: item.stock ?? 0,
    stock_en_transito: item.stock_en_transito ?? 0,
    equivalence: item.equivalence ?? null,
    venta_media_diaria: 0,
    // Valores mostrados: si no hay asignado, mostrar sugerido (misma semántica que la tabla).
    reorder_point:
      item.inventory_policy__reorder_point != null
        ? item.inventory_policy__reorder_point
        : (item.inventory_policy__reorder_point_suggested ?? 0),
    internal_safety_stock:
      item.inventory_policy__safety_stock != null && item.inventory_policy__safety_stock !== undefined
        ? item.inventory_policy__safety_stock
        : (item.inventory_policy__safety_stock_suggested ?? 0),
    inventory_policy__reorder_point: item.inventory_policy__reorder_point,
    inventory_policy__reorder_point_suggested: item.inventory_policy__reorder_point_suggested,
    inventory_policy__safety_stock: item.inventory_policy__safety_stock,
    inventory_policy__safety_stock_suggested: item.inventory_policy__safety_stock_suggested,
    __fromInventoryPolicies: true,
    cantidad_sugerida_sistema: 0,
    cantidad_sugerida_usuario: null,
    suggested_quantity_id: null,
    supplier_id: null,
    supplier_name: item.last_supplier ?? '',
    lead_time: item.inventory_policy__lead_time_days ?? 0,
    moq: 0,
    mp: 0,
    last_purchase_price: item.supplier_cost ?? 0,
    needs_reorder: false,
    unit_id: 0,
    unit_name: '',
    unit_abbreviation: '',
    supply_type: '',
    supply_type_id: null,
    on_demand: false,
    bom_need_week_min: null,
    is_validated: false,
    available_suppliers: [],
  });

  const handleProductRowClick = (item: ReorderPoint) => {
    setSelectedSidebarProduct(mapReorderPointToSidebarProduct(item));
    setIsSidebarOpen(true);
  };

  const excelNumberLocale = i18n.language?.toLowerCase().startsWith('it') ? 'it-IT' : 'es-ES';

  const exportStockStatusLabel = (item: ReorderPoint): string =>
    stockStatusLabel(item.stock_status_order, item.stock_status);

  const exportCoverageDaysValue = (item: ReorderPoint): number | null => {
    let coverageDays: number;
    if (item.coverage_days !== undefined && item.coverage_days >= 0) {
      coverageDays = item.coverage_days;
    } else {
      const workingDaysPerMonth = 30 * 5 / 7;
      const dailySales = item.monthly_avg_sales / workingDaysPerMonth;
      if (dailySales === 0 || item.monthly_avg_sales === 0) return null;
      const nonNegativeStock = Math.max(0, item.stock);
      coverageDays = (nonNegativeStock * workingDaysPerMonth) / item.monthly_avg_sales;
    }
    if (coverageDays < 0) return null;
    return coverageDays;
  };

  const tableHintHeader = (label: string, hintKey: string, align: 'left' | 'right') => (
    <div className={`flex items-center gap-0.5 ${align === 'right' ? 'justify-end' : ''}`}>
      <span>{label}</span>
      <span
        title={t(hintKey)}
        className="inline-flex shrink-0 cursor-help"
        aria-label={t(hintKey)}
      >
        <Info className="h-3 w-3 text-gray-400 transition-colors hover:text-blue-600" />
      </span>
    </div>
  );


  // Products table columns
  const productColumns = [
    {
      header: t('inventoryPolicies.table.code'),
      accessor: 'code',
      sortable: true,
      cell: (value: string, item: ReorderPoint) => {
        const eq = item.equivalence;
        const hasGroup = Boolean(eq?.members && eq.members.length > 1);
        const badgeTitle = hasGroup
          ? t('inventoryPolicies.equivalence.badgeTitle', {
              principal: eq?.principal_code || '—',
              members: (eq?.members || []).map((m) => m.code).join(', '),
            })
          : undefined;
        return (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-mono text-sm text-gray-900 truncate">{value}</span>
            {hasGroup ? (
              <span
                title={badgeTitle}
                className="shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-800 ring-1 ring-inset ring-sky-200"
              >
                {t('inventoryPolicies.equivalence.badge')}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      header: t('inventoryPolicies.table.product'),
      accessor: 'name',
      sortable: true
    },
    {
      header: t('inventoryPolicies.table.stockStatus'),
      accessor: 'stock_status',
      sortable: true,
      exportValue: (_value: string, item: ReorderPoint) => exportStockStatusLabel(item),
      cell: (value: string, item: any) => {
        // Usar el order del backend para determinar el color
        const getStatusColor = (statusName: string, order?: number) => {
          // Mapeo basado en el order del backend
          switch(order) {
            case 1: // Obsoleto
              return 'bg-red-100 text-red-800';
            case 2: // Sobre inventario
              return 'bg-yellow-100 text-yellow-800';
            case 3: // Óptimo
              return 'bg-green-100 text-green-800';
            case 4: // Bajo mínimos
              return 'bg-yellow-100 text-yellow-800';
            case 5: // Sin Stock
              return 'bg-red-100 text-red-800';
            default:
              // Fallback al mapeo original si no hay order
              const statusInfo = inventoryStatusMap[statusName as keyof typeof inventoryStatusMap];
              return statusInfo?.color || 'bg-gray-100 text-gray-800';
          }
        };

        const color = getStatusColor(value, item.stock_status_order);
        const displayValue = stockStatusLabel(item.stock_status_order, value);

        return (
          <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap ${color}`}>
            {displayValue}
          </span>
        );
      }
    },
    {
      header: t('inventoryPolicies.table.currentStock'),
      accessor: 'stock',
      sortable: true,
      align: 'right' as const,
      exportType: 'number' as const,
      cell: (value: number, item: ReorderPoint) => {
        const eq = item.equivalence;
        const hasGroup = Boolean(eq?.members && eq.members.length > 1);
        return (
          <div className="inline-flex items-center justify-end gap-1 font-variant-numeric tabular-nums text-gray-900">
            <span>{value !== null && value !== undefined ? value : '—'}</span>
            {hasGroup && eq ? (
              <span className="relative group inline-flex shrink-0">
                <span
                  className="inline-flex cursor-help"
                  aria-label={t('inventoryPolicies.equivalence.stockTooltipTitle')}
                >
                  <Info className="h-3.5 w-3.5 text-sky-600 transition-colors group-hover:text-sky-800" />
                </span>
                <span
                  role="tooltip"
                  className="pointer-events-none absolute right-0 bottom-full z-30 mb-1.5 hidden w-56 rounded-md border border-gray-200 bg-white p-2 text-left text-xs text-gray-700 shadow-lg group-hover:block"
                >
                  <span className="mb-1 block font-semibold text-gray-900">
                    {t('inventoryPolicies.equivalence.stockTooltipTitle')}
                  </span>
                  <span className="mb-1 flex justify-between gap-2 border-b border-gray-100 pb-1 font-medium text-gray-500">
                    <span>{t('inventoryPolicies.equivalence.stockCode')}</span>
                    <span>{t('inventoryPolicies.equivalence.stockQty')}</span>
                  </span>
                  <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                    {eq.members.map((m) => (
                      <li key={m.product_id} className="flex justify-between gap-2">
                        <span className="truncate font-mono">
                          {m.code}
                          {m.is_principal ? (
                            <span className="ml-1 font-sans text-[10px] text-sky-700">
                              ({t('inventoryPolicies.equivalence.principalMark')})
                            </span>
                          ) : null}
                        </span>
                        <span className="shrink-0 tabular-nums">{m.stock}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="mt-1.5 block border-t border-gray-100 pt-1 font-medium text-gray-900">
                    {t('inventoryPolicies.equivalence.groupTotal', {
                      total: eq.stock_group_total,
                    })}
                  </span>
                  {eq.conversion_pending ? (
                    <span className="mt-1 block text-[10px] leading-snug text-amber-700">
                      {t('inventoryPolicies.equivalence.conversionPending')}
                    </span>
                  ) : null}
                </span>
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      header: tableHintHeader(
        t('inventoryPolicies.table.monthlySales'),
        'inventoryPolicies.table.monthlySalesTooltip',
        'right',
      ),
      accessor: 'monthly_avg_sales',
      sortable: true,
      align: 'right' as const,
      exportHeader: t('inventoryPolicies.table.monthlySales'),
      exportType: 'number' as const,
      exportValue: (value: number) => (value ? Number(value) : null),
      cell: (value: number, item: ReorderPoint) => {
        if (!value) return <span className="text-xs text-gray-400">—</span>;
        const monthsUsed = item.monthly_avg_sales_months_used;
        const periodTooltip = !monthsUsed
          ? undefined
          : monthsUsed === 1
            ? t('inventoryPolicies.table.monthlySalesPeriodTooltipOne')
            : t('inventoryPolicies.table.monthlySalesPeriodTooltip', { months: monthsUsed });
        return (
          <div className="flex items-center justify-end gap-0.5 leading-tight font-variant-numeric tabular-nums">
            <div className="text-xs font-medium text-gray-900">
              {Number(value).toFixed(1)} {t('inventoryPolicies.table.unitsPerMonth')}
            </div>
            {periodTooltip ? (
              <span
                title={periodTooltip}
                className="inline-flex shrink-0 cursor-help"
                aria-label={periodTooltip}
              >
                <Info className="h-3 w-3 text-gray-400 transition-colors hover:text-blue-600" />
              </span>
            ) : null}
          </div>
        );
      }
    },
    {
      header: t('inventoryPolicies.table.coverage'),
      accessor: 'coverage_days',
      sortable: true,
      align: 'right' as const,
      exportType: 'number' as const,
      exportValue: (_value: unknown, item: ReorderPoint) => exportCoverageDaysValue(item),
      sortValue: (_value: any, item: ReorderPoint) => {
        // Usar coverage_days del backend si está disponible, sino calcular como fallback
        if (item.coverage_days !== undefined && item.coverage_days >= 0) {
          return item.coverage_days;
        }
        // Fallback: calcular en frontend (solo para compatibilidad)
        const workingDaysPerMonth = 30 * 5 / 7;
        const dailySales = item.monthly_avg_sales / workingDaysPerMonth;
        if (dailySales === 0 || item.monthly_avg_sales === 0) return -1;
        // Para cobertura, usar stock no negativo
        const nonNegativeStock = Math.max(0, item.stock);
        return (nonNegativeStock * workingDaysPerMonth) / item.monthly_avg_sales;
      },
      cell: (_value: any, item: ReorderPoint) => {
        // Usar coverage_days del backend si está disponible
        let coverageDays: number;
        if (item.coverage_days !== undefined && item.coverage_days >= 0) {
          coverageDays = Math.round(item.coverage_days);
        } else {
          // Fallback: calcular en frontend (solo para compatibilidad)
          const workingDaysPerMonth = 30 * 5 / 7;
          const dailySales = item.monthly_avg_sales / workingDaysPerMonth;
          if (dailySales === 0 || item.monthly_avg_sales === 0) {
            return <span className="text-gray-400 text-xs">-</span>;
          }
          // Para cobertura, usar stock no negativo
          const nonNegativeStock = Math.max(0, item.stock);
          coverageDays = Math.round((nonNegativeStock * workingDaysPerMonth) / item.monthly_avg_sales);
        }
        
        if (coverageDays < 0) {
          return <span className="text-gray-400 text-xs">-</span>;
        }
        
        // Usar el mismo color que el estado de stock basado en stock_status_order (solo texto, sin fondo)
        // Usar tonos más intensos y llamativos
        const getStatusTextColor = (order?: number) => {
          // Mapeo basado en el order del backend (mismo que stock_status)
          switch(order) {
            case 1: // Obsoleto
              return 'text-red-600 font-medium text-xs';
            case 2: // Sobre inventario
              return 'text-yellow-600 font-medium text-xs';
            case 3: // Óptimo
              return 'text-green-600 font-medium text-xs';
            case 4: // Bajo mínimos
              return 'text-yellow-600 font-medium text-xs';
            case 5: // Sin Stock
              return 'text-red-600 font-medium text-xs';
            default:
              return 'text-gray-600 font-medium text-xs';
          }
        };
        
        const colorClass = getStatusTextColor(item.stock_status_order);
        
        // Formatear con 2 decimales máximo (mostrar entero si no hay decimales)
        const formattedDays = coverageDays % 1 === 0 
          ? coverageDays.toString() 
          : coverageDays.toFixed(2);
        
        return (
          <span className={`block text-right font-variant-numeric tabular-nums ${colorClass}`}>
            {formattedDays} {t('inventoryPolicies.table.days')}
          </span>
        );
      }
    },
    {
      header: t('inventoryPolicies.table.purchaseFrequency'),
      accessor: 'supplier_purchase_frequency',
      sortable: true,
      align: 'right' as const,
      exportType: 'integer' as const,
      exportValue: (value: number) => value || 0,
      cell: (value: number) => (
        <span className="font-variant-numeric tabular-nums text-gray-700">
          {value || 0} {t('suppliers.days')}
        </span>
      ),
    },
    {
      header: t('inventoryPolicies.table.leadTime'),
      accessor: 'inventory_policy__lead_time_days',
      sortable: true,
      align: 'right' as const,
      exportType: 'integer' as const,
      exportValue: (value: number) => value || 0,
      cell: (value: number) => (
        <span className="font-variant-numeric tabular-nums text-gray-700">
          {value || 0} {t('suppliers.days')}
        </span>
      ),
    },
    {
      header: t('inventoryPolicies.table.supplierCost'),
      accessor: 'supplier_cost',
      sortable: true,
      align: 'right' as const,
      exportType: 'currency' as const,
      exportValue: (value: number) => {
        if (value === null || value === undefined || value === 0) return null;
        return Number(value);
      },
      cell: (value: number) => {
        if (value === null || value === undefined || value === 0) {
          return <span className="text-gray-400 text-xs">-</span>;
        }
        return (
          <span className="text-xs font-medium text-gray-900 font-variant-numeric tabular-nums">
            {formatMoney(value)}
          </span>
        );
      }
    },
    {
      header: t('inventoryPolicies.table.salesMonthsCurrentYear'),
      accessor: 'sales_months_current_year',
      align: 'right' as const,
      exportType: 'integer' as const,
      exportValue: (value: number | undefined) => value ?? 0,
      cell: (value: number | undefined) => (
        <span className="font-variant-numeric tabular-nums text-gray-700">
          {value ?? 0}
        </span>
      ),
    },
    {
      header: t('inventoryPolicies.table.salesMonthsTotal'),
      accessor: 'sales_months_total',
      align: 'right' as const,
      exportType: 'integer' as const,
      exportValue: (value: number | undefined) => value ?? 0,
      cell: (value: number | undefined) => (
        <span className="font-variant-numeric tabular-nums text-gray-700">
          {value ?? 0}
        </span>
      ),
    },
    {
      header: t('inventoryPolicies.table.firstSaleDate'),
      accessor: 'first_sale_date',
      align: 'right' as const,
      exportType: 'date' as const,
      exportValue: (value: string | null | undefined) => value || null,
      cell: (value: string | null | undefined) => {
        if (!value) {
          return <span className="text-gray-400 text-xs">-</span>;
        }
        return (
          <span className="text-xs text-gray-700 font-variant-numeric tabular-nums">
            {new Date(`${value}T00:00:00`).toLocaleDateString(excelNumberLocale)}
          </span>
        );
      },
    },
    {
      header: t('inventoryPolicies.table.actionCategory'),
      accessor: 'action_category_key',
      sortable: false,
      exportValue: (_value: string | null, item: ReorderPoint) => {
        const meta = resolveCategoryMeta(
          item.recommended_action,
          item.action_category_key,
          item.action_category_color ?? undefined,
        );
        if (!item.recommended_action && !item.action_category_key) return '—';
        return translateCategoryLabel(t, meta.categoryKey);
      },
      cell: (_value: string | null, item: ReorderPoint) => {
        const meta = resolveCategoryMeta(
          item.recommended_action,
          item.action_category_key,
          item.action_category_color ?? undefined,
        );
        if (!item.recommended_action && !item.action_category_key) {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        const label = translateCategoryLabel(t, meta.categoryKey);
        return (
          <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded whitespace-nowrap ${categoryBadgeClass(meta.color)}`}>
            {label}
          </span>
        );
      },
    },
    {
      header: t('inventoryPolicies.table.recommendedAction'),
      accessor: 'recommended_action',
      sortable: false,
      exportValue: (value: string | null) => translateActionLabel(t, value),
      cell: (value: string | null) => {
        const label = translateActionLabel(t, value);
        if (label === '—') {
          return <span className="text-gray-400 text-xs">—</span>;
        }
        return (
          <span className="block max-w-[200px] text-xs text-gray-800 line-clamp-2" title={label}>
            {label}
          </span>
        );
      },
    },
    {
      header: tableHintHeader(t('inventoryPolicies.table.reorderPointRecommended'), 'inventoryPolicies.tableHints.reorderSuggested', 'right'),
      exportHeader: t('inventoryPolicies.table.reorderPointRecommended'),
      accessor: 'inventory_policy__reorder_point_suggested',
      sortable: false, // Deshabilitado: requiere join complejo
      align: 'right' as const,
      exportType: 'number' as const,
      cell: (value: number) => (
        <span className="text-xs font-medium text-blue-600 font-variant-numeric tabular-nums">
          {value !== null && value !== undefined ? value : '-'}
        </span>
      )
    },
    {
      header: tableHintHeader(t('inventoryPolicies.table.safetyStockRecommended'), 'inventoryPolicies.tableHints.safetySuggested', 'right'),
      exportHeader: t('inventoryPolicies.table.safetyStockRecommended'),
      accessor: 'inventory_policy__safety_stock_suggested',
      sortable: false, // Deshabilitado: requiere join complejo
      align: 'right' as const,
      exportType: 'number' as const,
      cell: (value: number) => (
        <span className="text-xs font-medium text-blue-600 font-variant-numeric tabular-nums">
          {value !== null && value !== undefined ? value : '-'}
        </span>
      )
    },
    {
      header: tableHintHeader(t('inventoryPolicies.table.reorderPoint'), 'inventoryPolicies.tableHints.reorderAssigned', 'right'),
      exportHeader: t('inventoryPolicies.table.reorderPoint'),
      accessor: 'inventory_policy__reorder_point',
      sortable: false, // Deshabilitado: requiere join complejo
      align: 'right' as const,
      exportType: 'integer' as const,
      exportValue: (value: number | null) =>
        value !== null && value !== undefined ? value : null,
      cell: (value: number, item: ReorderPoint) => (
        editMode ? (
          <input
            type="number"
            className="w-20 rounded border border-gray-300 px-1.5 py-0.5 text-right text-xs font-variant-numeric tabular-nums"
            defaultValue={
              editedValues[item.id]?.reorder_point ?? (value ?? undefined)
            }
            placeholder={
              item.inventory_policy__reorder_point_suggested !== undefined && item.inventory_policy__reorder_point_suggested !== null
                ? String(item.inventory_policy__reorder_point_suggested)
                : undefined
            }
            min={0}
            onChange={(e) => handleEditChange(item.id, e.target.value)}
          />
        ) : (
          <span className="font-variant-numeric tabular-nums text-gray-900">{value ?? '—'}</span>
        )
      )
    },
    {
      header: t('inventoryPolicies.table.lastSupplier'),
      accessor: 'last_supplier',
      sortable: false, // Deshabilitado: campo calculado
      cell: (value: string) => {
        const name = value ?? '';
        if (!name) return <span className="text-gray-400 text-xs">-</span>;
        return (
          <span className="block truncate max-w-[140px] text-xs" title={name}>
            {name}
          </span>
        );
      }
    }
  ];



  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <h3 className="text-lg font-semibold mb-1">Error al cargar datos</h3>
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 space-y-6">
    {/* Espaciado vertical entre secciones */}
      
      <section className="rounded-lg border border-gray-200 bg-gray-50/90 p-3 shadow-sm">
        <FilterBar 
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        activeFilters={activeFilters}
        selectAllByFilter={selectAllByFilter}
        onClearFilter={handleClearFilter}
        onClearAllFilters={handleClearAllFilters}
        fetchOptionsForFilter={fetchProductOptionsForFilter}
        />
      </section>
  
      {warehousesLoading ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white py-16 text-gray-500">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#1681ba]" />
          <p className="mt-3 text-sm">{t('inventoryPolicies.emptyState.loadingWarehouses')}</p>
        </div>
      ) : warehouseId === null ? (
        <div className="rounded-lg border border-amber-100 bg-amber-50/90 px-6 py-14 text-center shadow-sm">
          <Building2 className="mx-auto h-12 w-12 text-amber-700/70" aria-hidden />
          <h2 className="mt-4 text-lg font-semibold text-gray-900">{t('inventoryPolicies.emptyState.title')}</h2>
          <p className="mx-auto mt-2 max-w-lg text-sm text-gray-600">{t('inventoryPolicies.emptyState.description')}</p>
        </div>
      ) : (
        <>
      {/* Layout: KPIs a la izquierda, gráfico de salud/historial a la derecha */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* KPIs a la izquierda (4 KPIs en 2 columnas) */}
        <div className="lg:col-span-2 grid min-h-[220px] grid-cols-2 gap-4">
          <KpiCard 
             title={t('inventoryPolicies.kpi.inventoryValue')}
             value={Math.max(0, Math.round(calculations?.total_inventory_value || 0))}
             isEuro
             icon={<DollarSign className="h-4 w-4" />}
             loading={kpisLoading}
          />
          <KpiCard 
             title={t('inventoryPolicies.kpi.monthsOfInventory')}
             value={`${Math.max(0, calculations?.months_of_inventory || 0).toFixed(2)}`}
             valueSuffix={t('inventoryPolicies.kpi.monthsUnit')}
             subtitle={t('inventoryPolicies.kpi.monthsOfInventoryFootnote', { months: trackingMonths })}
             subtitleClassName="mt-2 max-w-[12rem] text-center text-xs leading-snug text-gray-500"
             icon={<Calendar className="h-4 w-4" />}
             loading={kpisLoading}
          />
          {/* KPIs adicionales de salud del inventario */}
          {(!kpisLoading || calculations?.inventory_health) && (
            <>
              <KpiCard 
                variant="danger"
                title={stockStatusLabel(
                  5,
                  calculations?.inventory_health?.find((s) => s.status_order === 5)?.status,
                )}
                value={calculations?.inventory_health?.find(s => s.status_order === 5)?.count || 0}
                isCount
                loading={kpisLoading}
              />
              <KpiCard 
                variant="warning"
                title={stockStatusLabel(
                  2,
                  calculations?.inventory_health?.find((s) => s.status_order === 2)?.status,
                )}
                value={calculations?.inventory_health?.find(s => s.status_order === 2)?.count || 0}
                isCount
                loading={kpisLoading}
              />
            </>
          )}
        </div>

        {/* Gráfico a la derecha (salud de inventario o historial de movimientos) */}
        <div className="lg:col-span-3">
          {selectedProduct ? (
            /* Gráfico de historial de movimientos - solo cuando hay un producto filtrado */
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-medium text-gray-900">
                  {t('inventoryPolicies.movementHistory.title', { months: trackingMonths })}
                </h2>
                <button className="text-gray-500 hover:text-gray-700">
                  <Download className="h-4 w-4" />
                </button>
              </div>

              <div className="h-64">
                {historyLoading ? (
                  <div className="flex h-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#1681ba]" />
                  </div>
                ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={movementHistoryData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      domain={(() => {
                        if (movementHistoryData.length === 0) return [0, 100];
                        
                        const stockValues = movementHistoryData.map(item => item.stock);
                        const minValue = Math.min(...stockValues);
                        const maxValue = Math.max(...stockValues);
                        
                        // Agregar padding del 10% arriba y abajo
                        const padding = (maxValue - minValue) * 0.1;
                        // Permitir valores negativos en el eje Y
                        const yMin = minValue - padding;
                        const yMax = maxValue + padding;
                        
                        return [Math.floor(yMin), Math.ceil(yMax)];
                      })()}
                    />
                    <Tooltip 
                      labelFormatter={(date) => new Date(date).toLocaleDateString('es-ES')}
                      formatter={(value, name) => [
                        value,
                        name === 'stock' ? 'Stock' : name
                      ]}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="stock" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      name="Stock"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
                )}
              </div>

              <div className="mt-4 text-xs text-gray-500">
                {t('inventoryPolicies.movementHistory.footnote', { months: trackingMonths })}
              </div>
            </div>
          ) : (
            /* Gráfico de salud del inventario - cuando NO hay producto filtrado */
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-1 flex items-center gap-2">
                <h2 className="text-lg font-medium text-gray-900">
                  {t('inventoryPolicies.inventoryHealth.title')}
                </h2>
                {stockStatusFilterHelpList.intro || stockStatusFilterHelpList.items.length > 0 ? (
                  <FilterHelpIconPanel list={stockStatusFilterHelpList} />
                ) : null}
              </div>
              <p className="mb-3 text-xs text-gray-500">
                {t('inventoryPolicies.inventoryHealth.kpiWindowHint', { months: trackingMonths })}
              </p>
              {inventoryHealthForChart.length === 0 ? (
                <div className="flex min-h-[12rem] flex-col items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-4 py-10 text-center">
                  <p className="max-w-sm text-sm text-gray-600">{t('inventoryPolicies.inventoryHealth.chartEmpty')}</p>
                </div>
              ) : (
                <div
                  className="w-full"
                  style={{ height: Math.min(420, Math.max(220, inventoryHealthForChart.length * 44)) }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={inventoryHealthForChart}
                      margin={{ top: 8, right: 52, left: 8, bottom: 8 }}
                      barCategoryGap="20%"
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} stroke="#e5e7eb" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${v}%`}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={132}
                        tick={{ fontSize: 11, fill: '#374151' }}
                        tickLine={false}
                        axisLine={{ stroke: '#d1d5db' }}
                      />
                      <Tooltip
                        cursor={{ fill: 'rgba(22, 129, 186, 0.08)' }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          const row = payload[0].payload as {
                            name: string;
                            percentage: number;
                            count: number;
                          };
                          return (
                            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-xs shadow-lg">
                              <p className="font-semibold text-gray-900">{label}</p>
                              <p className="mt-0.5 text-gray-600">
                                {row.percentage}% ·{' '}
                                {t('inventoryPolicies.inventoryHealth.chartTooltipCount', { count: row.count })}
                              </p>
                              <p className="mt-1 text-[10px] font-medium text-[#1681ba]">
                                {t('inventoryPolicies.inventoryHealth.chartTooltipHint')}
                              </p>
                            </div>
                          );
                        }}
                      />
                      <Bar
                        dataKey="percentage"
                        radius={[0, 4, 4, 0]}
                        barSize={22}
                        onClick={(_, index: number) => {
                          const row = inventoryHealthForChart[index];
                          if (row?.status_order != null) {
                            handleHealthBarClick(row.status_order, row.status_id);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {inventoryHealthForChart.map((status, index) => (
                          <Cell key={`health-cell-${index}`} fill={inventoryHealthBarFill(status.status_order)} />
                        ))}
                        <LabelList
                          dataKey="percentage"
                          position="right"
                          formatter={(v: number) => `${v}%`}
                          style={{ fill: '#4b5563', fontSize: 11, fontWeight: 500 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-3 flex flex-col gap-1 border-t border-gray-100 pt-3 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
                <p className="text-xs text-gray-500">{t('inventoryPolicies.inventoryHealth.chartFootnote')}</p>
                {inventoryHealthTotalRefs > 0 ? (
                  <p className="text-xs font-medium tabular-nums text-gray-700">
                    {t('inventoryPolicies.inventoryHealth.chartTotal', { count: inventoryHealthTotalRefs })}
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      {!selectedProduct && showRecommendationsChart ? (
        <InventoryRecommendationChart
          bars={recommendationChartBars}
          loading={recommendationChartLoading}
          activeFilter={recommendationFilter}
          scopeFilterChips={recommendationChartScopeChips}
          onSegmentClick={handleRecommendationSegmentClick}
          onCategoryClick={handleRecommendationCategoryClick}
          onGlobalSegmentClick={handleGlobalRecommendationSegmentClick}
          onClearFilter={clearRecommendationFilter}
        />
      ) : null}

      {/* Detalle por producto con modo de edición */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
          <h2 className="text-sm font-semibold text-gray-900">{t('inventoryPolicies.productDetail.title')}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tablePositiveStockOnly}
                onChange={(e) => {
                  setTablePositiveStockOnly(e.target.checked);
                  setCurrentPage(1);
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
              />
              {t('inventoryPolicies.filters.positiveStockOnly')}
            </label>
            {warehouseId !== null && (
              <button
                onClick={() => {
                  if (editMode) {
                    setEditMode(false);
                    setEditedValues({});
                    setSaveSuccess(null);
                  } else {
                    setEditMode(true);
                  }
                }}
                className={`px-2.5 py-0.5 rounded text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1681ba] focus-visible:ring-offset-1 ${
                  editMode ? 'bg-gray-200 text-gray-800' : 'bg-[#1681ba] text-white'
                }`}
              >
                {editMode ? t('inventoryPolicies.reorderPoints.cancel') : t('inventoryPolicies.reorderPoints.editMin')}
              </button>
            )}
  
            {editMode && (
              <button
                onClick={handleSaveChanges}
                disabled={saving || Object.keys(editedValues).length === 0}
                className={`px-2.5 py-0.5 rounded text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-1 ${
                  saving || Object.keys(editedValues).length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {saving ? t('inventoryPolicies.reorderPoints.saving') : t('inventoryPolicies.reorderPoints.saveChanges')}
              </button>
            )}
          </div>
        </div>

        {/* Mensaje de éxito */}
        {saveSuccess && (
          <div className="mb-3 bg-green-50 border border-green-200 rounded p-2">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs font-medium text-green-800">{saveSuccess}</p>
              </div>
              <div className="ml-auto pl-2">
                <button
                  type="button"
                  onClick={() => setSaveSuccess(null)}
                  className="inline-flex rounded p-1 text-green-500 hover:bg-green-100 focus:outline-none"
                >
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
              </div>
            </div>
          </div>
        )}
  
        {/* Contenedor de tabla con overlay de loading manejado por DataTable */}
        <div className="relative">
          <DataTable
            columns={productColumns}
            data={calculations?.reorder_points || []}
            onRowClick={handleProductRowClick}
            compact
            resizableColumns
            pagination={false}
            loading={tableLoading}
            externalPagination={calculations?.pagination ? {
              currentPage: calculations.pagination.page,
              totalPages: calculations.pagination.total_pages,
              totalCount: calculations.pagination.total_count,
              pageSize: calculations.pagination.page_size,
              onPageChange: (page: number) => setCurrentPage(page),
              onPageSizeChange: (size: number) => {
                setPageSize(size);
                setCurrentPage(1);
              },
            } : undefined}
            externalSorting={{
              field: sorting.field,
              direction: sorting.direction,
              onSort: handleSort
            }}
            allowExport={true}
            exportFilename={t('inventoryPolicies.exportFilename')}
            onExport={async () => {
              // Función personalizada para exportar: obtener todos los datos con filtros aplicados
              const commonWarehouseId = warehouseId === null ? undefined : warehouseId;
              
              // Preparar parámetro de ordenamiento
              const orderingParam = sorting.field 
                ? `${sorting.direction === 'desc' ? '-' : ''}${sorting.field}`
                : undefined;
              
              // Petición con todos los datos (sin paginación) pero con filtros y ordenamiento
              const exportData = await inventoryService.getPolicyCalculations({
                ...policyApiFilters,
                product_id: selectedProduct,
                warehouse_id: commonWarehouseId,
                page: 1,
                page_size: 10000, // Activar get_all_products
                ordering: orderingParam,
                for_export: true, // Indicar que necesitamos datos completos
                sales_months: trackingMonths,
                ...(tablePositiveStockOnly ? { positive_stock_only: true } : {}),
                ...(typeof apiOrganizationId === 'number' ? { organization_id: apiOrganizationId } : {}),
              });
              
              return exportData.reorder_points || [];
            }}
          />
        </div>
        
      </div>
        </>
      )}

      <ProductDetailSidebar
        product={selectedSidebarProduct}
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedSidebarProduct(null);
        }}
      />
    </div>
  );
  
}
